import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// API & Hooks
import api from "@/api/axios";
import { API } from "@/App";
import { useAuth } from "@/hooks/AuthContext";

// shadcn/ui & icons
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, EyeOff, Pencil, PlusCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import RateHistoryEditor from "@/components/RateHistoryEditor";

// A small, self-contained component to handle rate visibility
const RateCell = ({ rate }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (rate === null || rate === undefined) {
    return <span className="text-muted-foreground">N/A</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="min-w-[60px] font-mono">
        {isVisible ? new Intl.NumberFormat("en-IN").format(rate) : "****"}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={(e) => {
          e.stopPropagation();
          setIsVisible(!isVisible);
        }}
      >
        {isVisible ? (
          <EyeOff className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
        <span className="sr-only">Toggle rate visibility</span>
      </Button>
    </div>
  );
};

// A reusable form for both adding and editing users
const UserForm = ({ userData, setUserData, isAdding = false }) => (
  <div className="grid gap-4 py-4 sm:grid-cols-2">
    <div>
      <Label htmlFor="name">Name</Label>
      <Input
        id="name"
        value={userData.name}
        onChange={(e) => setUserData({ ...userData, name: e.target.value })}
        placeholder="Full name"
      />
    </div>
    <div>
      <Label htmlFor="email">Email</Label>
      <Input
        id="email"
        type="email"
        value={userData.email}
        onChange={(e) => setUserData({ ...userData, email: e.target.value })}
        placeholder="you@example.com"
        disabled={false} // Always enabled now
      />
    </div>
    <div>
      <Label htmlFor="role">Role</Label>
      <Select
        value={userData.role}
        onValueChange={(v) => setUserData({ ...userData, role: v })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ADMIN">ADMIN</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
          <SelectItem value="TEAM">TEAM</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label htmlFor="job_title">Job Title</Label>
      <Input
        id="job_title"
        value={userData.job_title || ""}
        onChange={(e) =>
          setUserData({ ...userData, job_title: e.target.value })
        }
        placeholder="e.g. Architect"
      />
    </div>
    <div>
      <Label htmlFor="hourly_rate">Hourly Rate (INR)</Label>
      <Input
        id="hourly_rate"
        type="number"
        value={userData.hourly_rate || 0}
        onChange={(e) =>
          setUserData({ ...userData, hourly_rate: Number(e.target.value || 0) })
        }
      />
      {isAdding && (
        <p className="mt-1 text-xs text-muted-foreground">
          Applies from the join date. Later changes can be dated individually.
        </p>
      )}
    </div>
    {!isAdding && (
      <div>
        <Label htmlFor="rate_effective_from">Rate Effective From</Label>
        <Input
          id="rate_effective_from"
          type="date"
          value={userData.rate_effective_from || ""}
          onChange={(e) =>
            setUserData({ ...userData, rate_effective_from: e.target.value })
          }
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Timesheets are costed at this rate from this date on. Leave blank to
          apply from today. A past date re-costs that period.
        </p>
      </div>
    )}
    <div>
      <Label htmlFor="join_date">Join Date</Label>
      <Input
        id="join_date"
        type="date"
        value={userData.join_date || ""}
        onChange={(e) =>
          setUserData({ ...userData, join_date: e.target.value })
        }
      />
    </div>
    {!isAdding && (
      <div>
        <Label htmlFor="leave_date">Leave Date</Label>
        <Input
          id="leave_date"
          type="date"
          value={userData.leave_date || ""}
          onChange={(e) =>
            setUserData({ ...userData, leave_date: e.target.value })
          }
        />
      </div>
    )}
    <div className="sm:col-span-2">
      <Label htmlFor="qualifications">Qualifications (comma-separated)</Label>
      <Input
        id="qualifications"
        value={userData.qualifications_text || ""}
        onChange={(e) =>
          setUserData({ ...userData, qualifications_text: e.target.value })
        }
        placeholder="B.Arch, M.Arch"
      />
    </div>
    {isAdding ? (
      <div>
        <Label htmlFor="password">Temp Password</Label>
        <Input
          id="password"
          type="text"
          value={userData.password || ""}
          onChange={(e) =>
            setUserData({ ...userData, password: e.target.value })
          }
          placeholder="welcome123"
        />
      </div>
    ) : (
      <div>
        <Label htmlFor="is_active">Status</Label>
        <Select
          disabled={userData?.role === "ADMIN"}
          value={String(!!userData.is_active)}
          onValueChange={(v) =>
            setUserData({ ...userData, is_active: v === "true" })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
  </div>
);

function AdminHRPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Dialog and form states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const initialUserState = {
    name: "",
    email: "",
    role: "TEAM",
    job_title: "",
    hourly_rate: 1200,
    join_date: "",
    leave_date: "",
    qualifications_text: "",
    password: "",
  };
  const [newUserData, setNewUserData] = useState(initialUserState);
  const [editUserData, setEditUserData] = useState({});

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/users`);
      setRows(response.data);
      setErr("");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (user.role !== "ADMIN") {
      navigate("/team");
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const handleOpenEditDialog = (e, userToEdit) => {
    e.stopPropagation();
    setSelectedUser(userToEdit);
    setEditUserData({
      ...userToEdit,
      qualifications_text: (userToEdit.qualifications || []).join(", "),
      // Always blank on open: carrying a date over from a previous edit would
      // silently re-date a rate the admin did not mean to touch.
      rate_effective_from: "",
    });
    setIsEditDialogOpen(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserData.name || !newUserData.email) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Name and Email are required.",
      });
      return;
    }
    try {
      await api.post(`${API}/users`, {
        ...newUserData,
        job_title: newUserData.job_title || null,
        hourly_rate: Number(newUserData.hourly_rate || 0),
        join_date: newUserData.join_date || null,
        qualifications: (newUserData.qualifications_text || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        password: newUserData.password || null,
      });
      toast({ title: "Success", description: "New user has been created." });
      setIsAddDialogOpen(false);
      setNewUserData(initialUserState); // Reset form
      await loadUsers();
    } catch (ex) {
      toast({
        variant: "destructive",
        title: "Creation Failed",
        description:
          ex.response?.data?.detail || "An unexpected error occurred.",
      });
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    const payload = {
      name: editUserData.name,
      email: editUserData.email, // Include email in update
      role: editUserData.role,
      job_title: editUserData.job_title || null,
      hourly_rate: Number(editUserData.hourly_rate || 0),
      // Omitted rather than sent empty: the backend treats a present value as
      // "record this rate from that date", and null would fail validation.
      ...(editUserData.rate_effective_from
        ? { rate_effective_from: editUserData.rate_effective_from }
        : {}),
      join_date: editUserData.join_date || null,
      leave_date: editUserData.leave_date || null,
      qualifications: (editUserData.qualifications_text || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      is_active: !!editUserData.is_active,
    };

    try {
      await api.post(`${API}/users/${selectedUser.id}`, payload);
      toast({
        title: "Success",
        description: "User details have been updated.",
      });
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (ex) {
      toast({
        variant: "destructive",
        title: "Update Failed",
        description:
          ex.response?.data?.detail || "An unexpected error occurred.",
      });
    }
  };

  // Filter users into active and inactive lists
  const activeUsers = rows.filter((user) => user.is_active);
  const inactiveUsers = rows.filter((user) => !user.is_active);


  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Admin HR</h1>
          <p className="text-muted-foreground">
            Manage people, roles, and rates • Admin only
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" /> Add New Person
        </Button>
      </div>

      {/* Add User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Person</DialogTitle>
            <DialogDescription>
              Enter the details for the new team member. An invitation email
              will not be sent.
            </DialogDescription>
          </DialogHeader>
          <form id="add-user-form" onSubmit={handleCreateUser}>
            <UserForm
              userData={newUserData}
              setUserData={setNewUserData}
              isAdding={true}
            />
          </form>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" form="add-user-form">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {selectedUser?.name}</DialogTitle>
            <DialogDescription>
              Update the details for this team member.
            </DialogDescription>
          </DialogHeader>
          <form id="edit-user-form" onSubmit={handleUpdateUser}>
            <UserForm userData={editUserData} setUserData={setEditUserData} isAdding={false} />
          </form>

          {selectedUser?.id && (
            <div className="border-t pt-4">
              <RateHistoryEditor
                // Remounts per person so one user's history never shows under another.
                key={selectedUser.id}
                userId={selectedUser.id}
                userName={selectedUser.name}
                onChanged={(data) => {
                  setEditUserData((prev) => ({
                    ...prev,
                    hourly_rate: data.current_rate,
                    rate_effective_from: "",
                  }));
                  loadUsers();
                }}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" form="edit-user-form">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content Area */}
      {loading ? (
        <div className="text-muted-foreground text-center p-4">Loading...</div>
      ) : err ? (
        <div className="text-destructive text-center p-4">{err}</div>
      ) : (
        <div className="space-y-8">
          {/* Active Team Members Table */}
          <Card>
            <CardHeader>
              <CardTitle>Active Team Members</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Rate (INR/hr)</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeUsers.map((u) => (
                    <TableRow
                      key={u.id}
                      onClick={() => navigate(`/admin/hr/${u.id}`)}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {u.email}
                      </TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {u.job_title || "N/A"}
                      </TableCell>
                      <TableCell>
                        <RateCell rate={u.hourly_rate} />
                      </TableCell>
                      <TableCell>
                        {u.join_date
                          ? new Date(u.join_date).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={"default"}>Active</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleOpenEditDialog(e, u)}
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {activeUsers.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center text-muted-foreground"
                      >
                        No active people found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Inactive Members Graveyard Table */}
          {inactiveUsers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Inactive Members</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Exit Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inactiveUsers.map((u) => (
                      <TableRow key={u.id} onClick={() => navigate(`/admin/hr/${u.id}`)} className="cursor-pointer bg-muted/50">
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {u.email}
                        </TableCell>
                        <TableCell>{u.role}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {u.job_title || "N/A"}
                        </TableCell>
                        <TableCell>
                          {u.join_date
                            ? new Date(u.join_date).toLocaleDateString()
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={"secondary"}>Inactive</Badge>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">

                          {u.updatedAt
                            ? new Date(u.updatedAt).toLocaleDateString()
                            : "N/A"}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleOpenEditDialog(e, u)}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminHRPage;